import { db } from '../../config/firebase';
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
  Unsubscribe,
  getDocs
} from 'firebase/firestore';
import { AuditService } from './AuditService';

// ─── Types ───

export interface CoupleRecord {
  id: string;
  patient1Id: string;
  patient2Id: string;
  patient1Name: string;
  patient2Name: string;
  providerId: string;
  providerName: string;
  coupleLabel?: string;        // e.g., "Smith & Jones" — auto-generated or custom
  relationshipType: 'married' | 'partnered' | 'dating' | 'family' | 'other';
  status: 'active' | 'inactive' | 'discharged';
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

// ─── Helpers ───

function stripUndefined(obj: Record<string, any>): Record<string, any> {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined));
}

// ─── Service ───

export class FirebaseCoupleService {
  private static COLLECTION = 'couples';

  /**
   * Create a new couple record linking two patients.
   */
  static async createCouple(
    data: Omit<CoupleRecord, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    // Check if these two patients are already linked
    const existing = await this.findExistingCouple(data.patient1Id, data.patient2Id, data.providerId);
    if (existing) {
      throw new Error('These two clients are already linked as a couple.');
    }

    const docRef = doc(collection(db, this.COLLECTION));
    const label = data.coupleLabel || `${data.patient1Name} & ${data.patient2Name}`;

    await setDoc(docRef, {
      ...stripUndefined(data as any),
      coupleLabel: label,
      status: data.status || 'active',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    AuditService.logPhiModification(
      data.providerId, 'provider', 'create', 'form' as any, docRef.id, data.patient1Id,
      `Created couple record: ${label}`
    );

    return docRef.id;
  }

  /**
   * Update a couple record.
   */
  static async updateCouple(
    coupleId: string,
    providerId: string,
    updates: Partial<CoupleRecord>
  ): Promise<void> {
    const { id, createdAt, ...rest } = updates as any;
    await updateDoc(doc(db, this.COLLECTION, coupleId), {
      ...stripUndefined(rest),
      updatedAt: Timestamp.now(),
    });

    AuditService.logPhiModification(
      providerId, 'provider', 'update', 'form' as any, coupleId, '',
      'Updated couple record'
    );
  }

  /**
   * Dissolve a couple (set status to discharged).
   */
  static async dissolveCouple(coupleId: string, providerId: string): Promise<void> {
    await updateDoc(doc(db, this.COLLECTION, coupleId), {
      status: 'discharged',
      updatedAt: Timestamp.now(),
    });

    AuditService.logPhiModification(
      providerId, 'provider', 'update', 'form' as any, coupleId, '',
      'Dissolved couple record'
    );
  }

  /**
   * Check if two patients already have a couple record under this provider.
   */
  static async findExistingCouple(
    patientAId: string,
    patientBId: string,
    providerId: string
  ): Promise<CoupleRecord | null> {
    // Check both orderings
    const q1 = query(
      collection(db, this.COLLECTION),
      where('providerId', '==', providerId),
      where('patient1Id', '==', patientAId),
      where('patient2Id', '==', patientBId)
    );
    const q2 = query(
      collection(db, this.COLLECTION),
      where('providerId', '==', providerId),
      where('patient1Id', '==', patientBId),
      where('patient2Id', '==', patientAId)
    );

    const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);
    const allDocs = [...snap1.docs, ...snap2.docs].filter(d => d.data().status !== 'discharged');

    if (allDocs.length === 0) return null;
    const d = allDocs[0];
    const data = d.data();
    return {
      id: d.id,
      ...data,
      createdAt: data.createdAt?.toDate?.() || new Date(),
      updatedAt: data.updatedAt?.toDate?.() || new Date(),
    } as CoupleRecord;
  }

  /**
   * Subscribe to all couples for a provider (real-time).
   */
  static subscribeToProviderCouples(
    providerId: string,
    callback: (couples: CoupleRecord[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, this.COLLECTION),
      where('providerId', '==', providerId),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
      const couples = snapshot.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
        } as CoupleRecord;
      });
      callback(couples);
    });
  }

  /**
   * Subscribe to all couples involving any of the given patient IDs (practice-wide).
   * Uses two queries (patient1Id, patient2Id) and merges, deduplicating by doc ID.
   */
  static subscribeToCouplesByPatients(
    patientIds: string[],
    callback: (couples: CoupleRecord[]) => void
  ): Unsubscribe {
    if (patientIds.length === 0) {
      callback([]);
      return () => {};
    }

    // Firestore 'in' queries support max 30 values
    const ids = patientIds.slice(0, 30);

    let result1: CoupleRecord[] = [];
    let result2: CoupleRecord[] = [];

    const merge = () => {
      const seen = new Set<string>();
      const merged: CoupleRecord[] = [];
      for (const c of [...result1, ...result2]) {
        if (!seen.has(c.id)) {
          seen.add(c.id);
          merged.push(c);
        }
      }
      merged.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      callback(merged);
    };

    const mapDoc = (d: any): CoupleRecord => {
      const data = d.data();
      return {
        id: d.id,
        ...data,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date(),
      } as CoupleRecord;
    };

    const q1 = query(
      collection(db, this.COLLECTION),
      where('patient1Id', 'in', ids)
    );
    const q2 = query(
      collection(db, this.COLLECTION),
      where('patient2Id', 'in', ids)
    );

    const unsub1 = onSnapshot(q1, (snap) => {
      result1 = snap.docs.map(mapDoc);
      merge();
    });
    const unsub2 = onSnapshot(q2, (snap) => {
      result2 = snap.docs.map(mapDoc);
      merge();
    });

    return () => { unsub1(); unsub2(); };
  }

  /**
   * Check if a patient is part of any active couple.
   */
  static subscribeToPatientCouples(
    patientId: string,
    callback: (couples: CoupleRecord[]) => void
  ): Unsubscribe {
    // We need to query both patient1Id and patient2Id
    // Firestore doesn't support OR queries across fields, so use two listeners
    let couples1: CoupleRecord[] = [];
    let couples2: CoupleRecord[] = [];

    const q1 = query(
      collection(db, this.COLLECTION),
      where('patient1Id', '==', patientId),
      where('status', '==', 'active')
    );
    const q2 = query(
      collection(db, this.COLLECTION),
      where('patient2Id', '==', patientId),
      where('status', '==', 'active')
    );

    const unsub1 = onSnapshot(q1, (snap) => {
      couples1 = snap.docs.map(d => {
        const data = d.data();
        return { id: d.id, ...data, createdAt: data.createdAt?.toDate?.() || new Date(), updatedAt: data.updatedAt?.toDate?.() || new Date() } as CoupleRecord;
      });
      callback([...couples1, ...couples2]);
    });

    const unsub2 = onSnapshot(q2, (snap) => {
      couples2 = snap.docs.map(d => {
        const data = d.data();
        return { id: d.id, ...data, createdAt: data.createdAt?.toDate?.() || new Date(), updatedAt: data.updatedAt?.toDate?.() || new Date() } as CoupleRecord;
      });
      callback([...couples1, ...couples2]);
    });

    return () => { unsub1(); unsub2(); };
  }

  /**
   * Get the partner name for display purposes.
   */
  static getPartnerName(couple: CoupleRecord, currentPatientId: string): string {
    return couple.patient1Id === currentPatientId
      ? couple.patient2Name
      : couple.patient1Name;
  }

  /**
   * Get the partner ID.
   */
  static getPartnerId(couple: CoupleRecord, currentPatientId: string): string {
    return couple.patient1Id === currentPatientId
      ? couple.patient2Id
      : couple.patient1Id;
  }
}
